import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { UserPlus, Loader2 } from 'lucide-react';

const CreateRoomDialog = ({ 
  open, 
  onOpenChange, 
  api, 
  onCreateRoom 
}) => {
  const [newRoomName, setNewRoomName] = useState('');
  const [operators, setOperators] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    if (open && api) {
      let isMounted = true;
      const fetchOperators = async () => {
        try {
          const res = await api.get('/operators');
          if (isMounted) {
            setOperators(res.data);
          }
        } catch (err) {
          console.error('Failed to fetch operators:', err);
        } finally {
          if (isMounted) {
            setLoadingOperators(false);
          }
        }
      };
      setLoadingOperators(true);
      fetchOperators();
      return () => { isMounted = false; };
    }
  }, [open, api]);

  const toggleOperator = (operatorId) => {
    setSelectedOperators(prev => 
      prev.includes(operatorId)
        ? prev.filter(id => id !== operatorId)
        : [...prev, operatorId]
    );
  };

  const handleCreate = async () => {
    if (!newRoomName.trim()) return;

    try {
      await onCreateRoom(newRoomName.trim(), 'general', null, selectedOperators);
      handleClose();
    } catch (err) {
      console.error('Failed to create room:', err);
    }
  };

  const handleClose = () => {
    setNewRoomName('');
    setSelectedOperators([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleClose();
      else onOpenChange(isOpen);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Chat Room</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="room-name">Room Name</Label>
            <Input
              id="room-name"
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              placeholder="e.g., Team Chat, Site A Discussion"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Add Participants (Operators)
            </Label>
            {loadingOperators ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : operators.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No operators available</p>
            ) : (
              <ScrollArea className="h-40 border rounded-md p-2">
                <div className="space-y-2">
                  {operators.map((op) => (
                    <div
                      key={op.id}
                      className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                      onClick={() => toggleOperator(op.id)}
                    >
                      <Checkbox
                        id={`op-${op.id}`}
                        checked={selectedOperators.includes(op.id)}
                        onCheckedChange={() => toggleOperator(op.id)}
                      />
                      <label
                        htmlFor={`op-${op.id}`}
                        className="text-sm font-medium leading-none cursor-pointer flex-1"
                      >
                        {op.name}
                      </label>
                      {op.role && (
                        <Badge variant="secondary" className="text-xs">
                          {op.role}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            {selectedOperators.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {selectedOperators.length} operator(s) selected
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!newRoomName.trim()}>
            Create Room
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRoomDialog;
